/**
 * Physical Price — Access `frmFuturesQryLocalPriceAdd` + datasheet `frmFuturesQryLocalPriceAdd_subForm`
 * (`form_frmFuturesQryLocalPriceAdd.txt`, `form_frmFuturesQryLocalPriceAdd_subForm.txt`).
 * Layout/styling: `/sales/contract/list` ContractFormDialog pattern (`futuresSaleContractDialog*`).
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import DataTableMUI from '../../components/DataTableMUI'
import DateTextField from '../../components/DateTextField'
import NumericInput from './NumericInput'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  useGetFuturesLocalPricePriceUnitsQuery,
  useLazyGetFuturesLocalPriceDraftQuery,
  useSaveFuturesLocalPriceMutation,
} from '../../store/api/futuresControlApi'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleTextSx,
  futuresSaleContractDialogContentSx,
  futuresSaleContractDialogPaperProps,
  futuresSaleContractDialogTitleSx,
} from './futuresAccessUi'

interface Props {
  open: boolean
  onClose: () => void
  /** Giống Access `SCREEN_ACCESS = 1` — nút Save Price. */
  canSave: boolean
}

type PhysicalPriceGridRow = {
  id: string
  dateActual: string
  qualityCode: string
  price: number | null
  priceUnit: string
} & Record<string, unknown>

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10)
}

export default function FuturesLocalPriceDialog({ open, onClose, canSave }: Props) {
  const snack = useSnackbar()
  const [asOfDate, setAsOfDate] = useState(todayIsoDate)
  const [fxStr, setFxStr] = useState('')
  const [rows, setRows] = useState<PhysicalPriceGridRow[]>([])
  const [touched, setTouched] = useState(false)

  const { data: unitsPayload } = useGetFuturesLocalPricePriceUnitsQuery(undefined, { skip: !open })
  const unitOptions = useMemo(() => {
    const u = unitsPayload?.options?.length ? unitsPayload.options : ['VND/KG']
    return u.map((v) => ({ value: v, label: v }))
  }, [unitsPayload?.options])

  const [loadDraft, draftResult] = useLazyGetFuturesLocalPriceDraftQuery()
  const { data, isFetching, isError } = draftResult

  const [savePrice, { isLoading: saving }] = useSaveFuturesLocalPriceMutation()

  useEffect(() => {
    if (open) {
      setAsOfDate(todayIsoDate())
      setTouched(false)
    }
  }, [open])

  useEffect(() => {
    if (!open || !asOfDate.trim()) return
    void loadDraft({ inputDate: asOfDate })
  }, [open, asOfDate, loadDraft])

  useEffect(() => {
    if (!data?.rows) {
      setRows([])
      return
    }
    setRows(
      data.rows.map((r) => ({
        id: r.qualityCode,
        dateActual: r.dateActual,
        qualityCode: r.qualityCode,
        price: r.price ?? null,
        priceUnit: (r.priceUnit || 'VND/KG').trim() || 'VND/KG',
      })),
    )
    if (data.fx != null && !Number.isNaN(Number(data.fx))) {
      setFxStr(String(data.fx))
    } else {
      setFxStr('')
    }
  }, [data])

  const handleRowUpdate = useCallback((newRow: PhysicalPriceGridRow) => {
    setRows((prev) => prev.map((r) => (r.id === newRow.id ? { ...newRow } : r)))
    return newRow
  }, [])

  const columns: GridColDef<PhysicalPriceGridRow>[] = useMemo(
    () => [
      {
        field: 'priceUnit',
        headerName: 'Unit',
        width: 130,
        editable: true,
        type: 'singleSelect',
        valueOptions: unitOptions,
      },
      {
        field: 'dateActual',
        headerName: 'Date Actual',
        minWidth: 130,
        flex: 0.6,
        editable: false,
      },
      {
        field: 'qualityCode',
        headerName: 'Quality Code',
        minWidth: 140,
        flex: 0.8,
        editable: false,
      },
      {
        field: 'price',
        headerName: 'Physical Price (EDIT)',
        width: 170,
        editable: true,
        type: 'number',
      },
    ],
    [unitOptions],
  )

  const handleSave = async () => {
    if (!canSave) {
      snack.error("You don't have permission for this action.")
      return
    }
    setTouched(true)
    if (!asOfDate.trim()) {
      snack.error('Select Date Actual.')
      return
    }
    let fxVal: number | null = null
    const t = fxStr.trim()
    if (t !== '') {
      const n = Number(t)
      if (Number.isNaN(n)) {
        snack.error('Invalid FX rate.')
        return
      }
      fxVal = n
    }
    try {
      await savePrice({
        inputDate: asOfDate,
        fx: fxVal,
        rows: rows.map(({ dateActual, qualityCode, price, priceUnit }) => ({
          dateActual,
          qualityCode,
          price,
          priceUnit,
        })),
      }).unwrap()
      snack.success('Saved.')
      onClose()
    } catch (e: unknown) {
      const detail =
        typeof e === 'object' && e !== null && 'data' in e
          ? (e as { data?: { detail?: string } }).data?.detail
          : undefined
      snack.error(detail?.trim() || 'Could not save.')
    }
  }

  const loadMessage = isError ? 'Failed to load data.' : null

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper" PaperProps={futuresSaleContractDialogPaperProps}>
      <DialogTitle sx={futuresSaleContractDialogTitleSx}>
        <Typography component="span" noWrap title="Physical Price" sx={futuresFormDialogTitleTextSx}>
          Physical Price
        </Typography>
        <Box sx={futuresFormDialogActionsBoxSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave || saving || isFetching}
            sx={futuresFormDialogActionBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save Price'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} sx={futuresFormDialogActionBtnSx}>
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
        {!canSave ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            Read-only — you don't have edit permission for this module.
          </Alert>
        ) : null}
        {loadMessage ? (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {loadMessage}
          </Typography>
        ) : null}

        <Box sx={{ maxWidth: 1100, mx: 'auto', pb: 1 }}>
          <Grid container spacing={0.75} sx={{ mb: 1.25 }}>
            <Grid item xs={12} sm={6} md={4}>
              <DateTextField
                label="Date Actual *"
                size="small"
                fullWidth
                value={asOfDate}
                onChange={(v) => setAsOfDate(v || todayIsoDate())}
                nativeInputProps={{ 'aria-label': 'Date Actual' }}
                error={touched && !asOfDate.trim()}
                helperText={touched && !asOfDate.trim() ? 'Please choose Date Actual' : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <NumericInput
                label="FX"
                size="small"
                fullWidth
                value={fxStr}
                onChange={setFxStr}
                inputProps={{ 'aria-label': 'FX' }}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ width: '100%' }}>
                <Button variant="outlined" size="small" onClick={() => void loadDraft({ inputDate: asOfDate })} disabled={isFetching}>
                  {isFetching ? <CircularProgress size={14} color="inherit" /> : 'Load'}
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, minHeight: '1.2em' }}>
            {isFetching ? 'Loading…' : ''}
          </Typography>

          <DataTableMUI<PhysicalPriceGridRow>
            rows={rows}
            columns={columns}
            height={minGridHeight(rows.length)}
            processRowUpdate={(nr) => handleRowUpdate(nr as PhysicalPriceGridRow)}
            onProcessRowUpdateError={() => snack.error('Could not update cell.')}
            isCellEditable={() => !isFetching}
            loading={isFetching}
            disableColumnMenu
            hideFooter={rows.length <= 25}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

function minGridHeight(rowCount: number): number {
  const h = 120 + Math.min(Math.max(rowCount, 1), 25) * 36
  return Math.min(Math.max(h, 320), 520)
}
